import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import {
  ApiRecord,
  controlId,
  controlReasonAction,
  controlReasonActionMetadata,
  controlStatus,
  documentId,
  documentStatus,
  packageId,
  packageName,
  packageStatus,
  recordsFromResponse,
  text,
  statusPillClass,
  valueOrDash,
} from './packageUi';
import { useClientAccount } from '../account/AccountContext';

type LoadState = {
  loading: boolean;
  packages: ApiRecord[];
  controls: ApiRecord[];
  documents: ApiRecord[];
  actions: ApiRecord[];
  error?: string;
};

function packageActionText(pkg: ApiRecord): string {
  const category = text(pkg, 'actionCategory').toUpperCase();
  if (category === 'STALE_FINAL_AUDITOR_PACKAGE') {
    const finalUpdated = formatLocalDateTime(text(pkg, 'finalAuditorPackageUpdatedAt'));
    const technicalUpdated = formatLocalDateTime(text(pkg, 'latestTechnicalPackageUpdatedAt'));
    const workflowId = text(pkg, 'technicalWorkflowId');
    const workflowSuffix = workflowId ? ` Technical workflow ID: ${workflowId}.` : '';
    return `A newer technical baseline was generated after the current final auditor package. Final auditor package: ${finalUpdated}. Latest technical baseline: ${technicalUpdated}.${workflowSuffix} Review the latest technical baseline and generate a new final auditor package before assessor use.`;
  }
  return text(pkg, 'reasonAction', 'recommendedAction', 'actionRequiredReason', 'statusReason', 'reason');
}

export default function ActionsRequiredPage() {
  const { accountPath } = useClientAccount();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [state, setState] = useState<LoadState>({ loading: true, packages: [], controls: [], documents: [], actions: [] });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await api.get<unknown>('/client/actions-required');
        if (!cancelled) {
          setState({
            loading: false,
            packages: recordsFromResponse(response, ['packages', 'packageActions']),
            controls: recordsFromResponse(response, ['controls', 'controlActions']),
            documents: recordsFromResponse(response, ['documents', 'documentActions']),
            actions: recordsFromResponse(response, ['actions', 'actionsRequired', 'items']),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            packages: [],
            controls: [],
            documents: [],
            actions: [],
            error: error instanceof Error ? error.message : 'Unable to load actions required.',
          });
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const categorizedActionCount = state.packages.length + state.controls.length + state.documents.length;
  const totalActions = Math.max(categorizedActionCount, state.actions.length);

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Actions Required</h2>
        <p>Client-owned evidence, control, and final review items that need attention before assessor use.</p>
      </div>

      {state.loading && <article className="card"><p>Loading actions required...</p></article>}
      {state.error && <article className="card"><p className="error-text">{state.error}</p></article>}

      {!state.loading && !state.error && (
        <>
          <div className="card-grid">
            <article className="metric-card metric-warning"><span>Total action items</span><strong>{totalActions}</strong></article>
            <article className="metric-card metric-blue"><span>Package actions</span><strong>{state.packages.length}</strong></article>
            <article className="metric-card metric-muted"><span>Control actions</span><strong>{state.controls.length}</strong></article>
            <article className="metric-card metric-muted"><span>Document actions</span><strong>{state.documents.length}</strong></article>
          </div>

          {totalActions === 0 && <article className="card"><p>No client actions are currently required.</p></article>}

          {state.packages.length > 0 && (
            <article className="card">
              <h3>Package actions</h3>
              <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th>Package</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {state.packages.map((pkg, index) => {
                      const id = packageId(pkg);
                      return (
                        <tr key={id || index}>
                          <td>{id ? <Link className="table-link" to={accountPath(`/evidence-packages/${encodeURIComponent(id)}`)}>{packageName(pkg)}</Link> : packageName(pkg)}</td>
                          <td><span className={statusPillClass(packageStatus(pkg))}>{packageStatus(pkg)}</span></td>
                          <td>{valueOrDash(packageActionText(pkg))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </article>
          )}

          {state.controls.length > 0 && (
            <article className="card">
              <h3>Control actions</h3>
              <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th>Control</th><th>Status</th><th>Package</th><th>Action</th></tr></thead>
                  <tbody>
                    {state.controls.map((control, index) => {
                      const id = text(control, 'packageId');
                      return (
                        <tr key={`${controlId(control)}-${index}`}>
                          <td>{valueOrDash(controlId(control))}</td>
                          <td><span className={statusPillClass(controlStatus(control))}>{controlStatus(control)}</span></td>
                          <td>{id ? <Link className="table-link" to={accountPath(`/evidence-packages/${encodeURIComponent(id)}`)}>{id}</Link> : '—'}</td>
                          <td>
                            <div>{valueOrDash(controlReasonAction(control))}</div>
                            {controlReasonActionMetadata(control) && <small className="muted-text">{controlReasonActionMetadata(control)}</small>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </article>
          )}

          {state.documents.length > 0 && (
            <article className="card">
              <h3>Document actions</h3>
              <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th>Document</th><th>Status</th><th>Package</th></tr></thead>
                  <tbody>
                    {state.documents.map((document, index) => {
                      const id = text(document, 'packageId');
                      return (
                        <tr key={`${documentId(document)}-${index}`}>
                          <td>{valueOrDash(documentId(document))}</td>
                          <td><span className={statusPillClass(documentStatus(document))}>{documentStatus(document)}</span></td>
                          <td>{id ? <Link className="table-link" to={accountPath(`/evidence-packages/${encodeURIComponent(id)}`)}>{id}</Link> : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </article>
          )}
        </>
      )}
    </div>
  );
}
